import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth, ROLES } from '../contexts/AuthContext';
import { Loader2 } from 'lucide-react';

// Loading component
const LoadingSpinner = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="text-center">
      <Loader2 className="h-12 w-12 animate-spin text-blue-600 mx-auto" />
      <p className="mt-4 text-gray-600">Yükleniyor...</p>
    </div>
  </div>
);

// Access Denied component
const AccessDenied = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="text-center">
      <div className="text-6xl mb-4">🚫</div>
      <h1 className="text-2xl font-bold text-gray-900 mb-2">Erişim Engellendi</h1>
      <p className="text-gray-600 mb-4">Bu sayfayı görüntüleme yetkiniz bulunmamaktadır.</p>
      <a href="/dashboard" className="text-blue-600 hover:underline">Dashboard'a Dön</a>
    </div>
  </div>
);

/**
 * ProtectedRoute - Authentication and role-based access control
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child components to render
 * @param {string[]} [props.allowedRoles] - Array of allowed roles (optional)
 * @param {boolean} [props.requireAdmin] - Shortcut for requiring super_admin or site_manager
 * @param {boolean} [props.requireSuperAdmin] - Shortcut for requiring only super_admin
 */
const ProtectedRoute = ({ 
  children, 
  allowedRoles,
  requireAdmin = false,
  requireSuperAdmin = false
}) => {
  const { user, loading, isAuthenticated } = useAuth();
  const location = useLocation();

  // Show loading while checking auth
  if (loading) {
    return <LoadingSpinner />;
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check role-based access
  let hasAccess = true;

  if (requireSuperAdmin) {
    hasAccess = user?.role === ROLES.SUPER_ADMIN;
  } else if (requireAdmin) {
    hasAccess = user?.role === ROLES.SUPER_ADMIN || user?.role === ROLES.ADMIN || user?.role === ROLES.SITE_MANAGER || user?.role === ROLES.HESAP_YONETICISI;
  } else if (allowedRoles && allowedRoles.length > 0) {
    hasAccess = allowedRoles.includes(user?.role);
  }

  if (!hasAccess) {
    // Site Sakini'yi kendi dashboard'una yönlendir
    if (user?.role === ROLES.SITE_SAKINI || user?.role === ROLES.USER || user?.role === ROLES.SAKINI) {
      return <Navigate to="/resident/dashboard" replace />;
    }
    // Diğer yetkisiz kullanıcıları normal dashboard'a yönlendir
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Higher-order component for protecting components
export const withAuth = (Component, options = {}) => {
  return function ProtectedComponent(props) {
    return (
      <ProtectedRoute {...options}>
        <Component {...props} />
      </ProtectedRoute>
    );
  };
};

// Role-specific route wrappers for convenience
export const AdminRoute = ({ children }) => (
  <ProtectedRoute requireAdmin>
    {children}
  </ProtectedRoute>
);

export const SuperAdminRoute = ({ children }) => (
  <ProtectedRoute requireSuperAdmin>
    {children}
  </ProtectedRoute>
);

export default ProtectedRoute;
